import { PropsWithChildren, useState, useMemo } from 'react';

import createSafeContext from '@paradigm/utils/createSafeContext';
import { Maybe } from '@paradigm/utils/types';
import { PricingResp } from '#/unified-rfqs/repositories/drfq-types';
import { useQuoteBuilderActions } from '#/unified-rfqs/ui/drfq/quote-builder/form-context';

import { Side } from '#/unified-rfqs/entities/shared';
import { useQuoteBuilderModalMethods } from './modal-context';
import { useQuoteBuilderSubmit } from './submit-context';

type ReportCb = (report: { errors: string[] }) => void;
interface EstimatedPriceModalMethods {
  readonly open: (params: OpenParams) => void;
  readonly close: () => void;
  readonly submit: (cb: ReportCb) => void;
  readonly editLegPrices: () => void;
}

interface OpenParams {
  readonly estimatedPricesResp: PricingResp;
}

interface EstimatedPriceModalState {
  readonly isOpen: boolean;
  readonly isLoading: boolean;
  readonly estimatedPrices?: Maybe<PricingResp>;
}

const [useEstimatedPriceModalState, ModalStateProvider] =
  createSafeContext<EstimatedPriceModalState>('EstimatedPriceModalState');

const [useEstimatedPriceModalMethods, ModalMethodsProvider] =
  createSafeContext<EstimatedPriceModalMethods>('EstimatedPriceModalMethods');

export { useEstimatedPriceModalState, useEstimatedPriceModalMethods };
export default function EstimatedPriceModalProvider({
  children,
}: PropsWithChildren) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedPrices, setEstimatedPrices] = useState<Maybe<PricingResp>>();
  const { setLegPricing, setIsMarkPricePrefill } =
    useQuoteBuilderModalMethods();
  const { resetLegPrices, fillLegPricesWithMarkPrice } =
    useQuoteBuilderActions();
  const { createQuoteByLegPrices } = useQuoteBuilderSubmit();

  const actions = useMemo(
    () => ({
      open: (params: OpenParams) => {
        setEstimatedPrices(params.estimatedPricesResp);
        setIsOpen(true);
      },
      close: () => setIsOpen(false),
      submit: (cb: ReportCb) => {
        if (estimatedPrices == null) return;

        setIsLoading(true);

        createQuoteByLegPrices({
          askPrices: estimatedPrices.ask_prices,
          bidPrices: estimatedPrices.bid_prices,
        }).then(([bidResp, askResp]) => {
          const errors: string[] = [];
          if (bidResp?.ok === false) errors.push(bidResp.message);
          if (askResp?.ok === false) errors.push(askResp.message);

          cb({ errors });
          setIsLoading(false);
        });
      },
      editLegPrices: () => {
        if (estimatedPrices == null) return;
        const { bid_prices, ask_prices } = estimatedPrices;
        setLegPricing(true);
        setIsMarkPricePrefill(true);
        resetLegPrices();
        const sides: Side[] = [];
        if (bid_prices.length > 0) sides.push('BUY');
        if (ask_prices.length > 0) sides.push('SELL');
        fillLegPricesWithMarkPrice(sides);
        setIsOpen(false);
      },
    }),
    [
      createQuoteByLegPrices,
      estimatedPrices,
      fillLegPricesWithMarkPrice,
      resetLegPrices,
      setIsMarkPricePrefill,
      setLegPricing,
    ],
  );

  return (
    <ModalStateProvider
      value={{
        isOpen,
        isLoading,
        estimatedPrices,
      }}
    >
      <ModalMethodsProvider value={actions}>{children}</ModalMethodsProvider>
    </ModalStateProvider>
  );
}
